import React, { useState, useEffect, useMemo } from "react";
import Navbar from "./Navbar";
import TripStepperTimeline from "./components/TripStepperTimeline";
import ActiveRidePage from "./ActiveRidePage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faShop,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import { CircleCheck, ArrowRight, Headphones } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import { decryptData } from "../utils/storageCrypto";
import { theme } from "../assets/theme/theme";
import SwipeButton from "./components/SwipeButton";
import HelpSupportModal from "./components/HelpSupport";
import BB from "./Images/BB.png";

const Rides = () => {
  const [mockRides, setMockRides] = useState([]);
  const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mobile_number = queryParams.get("mobile");
  const { notifications, setNotifications } = useNotification();
  const [showSupportModal, setShowSupportModal] = useState(false);

  const [activeRide, setActiveRide] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  console.log(currentLocation);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [isTripCompleteLocal, setIsTripCompleteLocal] = useState(false);

  // Success Acknowledgement State
  const [acceptedTripData, setAcceptedTripData] = useState(null);
  const [isProceeding, setIsProceeding] = useState(false);

  // Controls whether the driver has tapped "Start Trip" on the Overview screen (Screen 1)
  const [isTripRouteStarted, setIsTripRouteStarted] = useState(() => {
    return sessionStorage.getItem("trip_route_started") === "true";
  });

  useEffect(() => {
    if (mobile_number) {
      loadActiveRide(mobile_number);
    }
    requestLiveLocation();
  }, [mobile_number]);

  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return parseFloat((R * c).toFixed(1));
  };

const requestLiveLocation = () => {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported by this browser");
      setCurrentLocation({ latitude: 16.5062, longitude: 80.648 });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        console.warn("Geolocation error:", error.message);
        
        // If the user previously tapped 'Block', inform them how to unblock
        if (error.code === error.PERMISSION_DENIED) {
          console.warn("Location permission was denied. Tap the site settings icon in the URL bar to allow location.");
        }

        // Set fallback near active pickup stop or city hub if GPS was denied
        setCurrentLocation({ latitude: 16.5062, longitude: 80.648 });
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  const calculatePickupDistance = (ride) => {
    if (!currentLocation || !ride?.route?.pickups?.length) return "N/A";
    const firstPickup = ride.route.pickups.find(
      (stop) => Number(stop.stopSequence) === 1
    );
    if (!firstPickup || !firstPickup.latitude || !firstPickup.longitude) {
      return "N/A";
    }
    const distance = haversineDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      firstPickup.latitude,
      firstPickup.longitude
    );
    return `${distance} km`;
  };

  const formatRideToActiveRide = (ride) => {
    if (ride.steps && ride.steps.length > 0) return { ...ride, tripId: ride.tripId || ride._id };

    const steps = [];
    if (ride.route?.pickups) {
      ride.route.pickups.forEach((p) =>
        steps.push({ ...p, type: "PICKUP", status: "PENDING", step: p.stopSequence })
      );
    }
    if (ride.route?.dropoffs) {
      ride.route.dropoffs.forEach((d) =>
        steps.push({ ...d, type: "DROPOFF", status: "PENDING", step: d.stopSequence })
      );
    }

    steps.sort((a, b) => (a.stopSequence || 0) - (b.stopSequence || 0));

    return { ...ride, steps, tripId: ride.tripId || ride._id };
  };

  const loadActiveRide = async (mobile) => {
    const active = await fetchActiveRide(mobile);

    const isCompleted =
      active?.status?.toLowerCase() === "completed" ||
      (active?.steps?.length > 0 &&
        active.steps.every(
          (s) =>
            s.status === "COMPLETED" ||
            s.status === "SKIPPED" ||
            (s.type === "PICKUP" &&
              (s.status === "IN_PROGRESS" || s.status === "IN PROGESS"))
        ));

    if (active && !isCompleted) {
      setActiveRide(active);
      setIsTripCompleteLocal(false);

      const wasRouteStartedInSession =
        sessionStorage.getItem("trip_route_started") === "true";

      const hasActualStartedLeg = active.steps?.some((s) => {
        const st = s.status ? String(s.status).toUpperCase() : "";
        return (
          st === "STARTED" ||
          st === "IN_TRANSIT" ||
          st === "IN_PROGRESS" ||
          st === "IN PROGESS" ||
          st === "OTP_GENERATED" ||
          st === "COMPLETED"
        );
      });

      if (wasRouteStartedInSession || hasActualStartedLeg) {
        sessionStorage.setItem("trip_route_started", "true");
        setIsTripRouteStarted(true);
      } else {
        sessionStorage.removeItem("trip_route_started");
        setIsTripRouteStarted(false);
      }
    } else {
      setActiveRide(null);
      setIsTripCompleteLocal(false);
      sessionStorage.removeItem("trip_route_started");
      setIsTripRouteStarted(false);
      fetchRides(mobile);
    }
  };

  const fetchActiveRide = async (mobile) => {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);
    try {
      if (!mobile) throw new Error("Mobile number is required");

      const response = await fetch(
        `${URL}delivery-partner/get-active-ride?mobile=${mobile}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
        }
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch active ride");
      }

      let activeData = result.data;
      if (Array.isArray(activeData)) {
        activeData = activeData.length > 0 ? activeData[0] : null;
      }

      return activeData;
    } catch (error) {
      console.error("Error fetching active ride:", error.message);
      return null;
    }
  };

  const fetchRides = async (mobile) => {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);
    try {
      if (!mobile) throw new Error("Mobile number is required");

      const response = await fetch(
        `${URL}delivery-partner/get-rides?mobile=${mobile}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const ridesList = data.data || [];

      const assignedRide = ridesList.find((r) => {
        const isAssignedStatus =
          r.status?.toUpperCase() === "ASSIGNED" ||
          r.assignmentStatus?.toUpperCase() === "ASSIGNED";
        const isMyRide = r.rider && String(r.rider.mobile) === String(mobile);
        const isRiderAssigned = r.rider?.assigned === true;

        return isAssignedStatus && isMyRide && isRiderAssigned;
      });

      if (assignedRide) {
        setActiveRide(formatRideToActiveRide(assignedRide));
        setMockRides([]);
        setIsTripCompleteLocal(false);
        sessionStorage.removeItem("trip_route_started");
        setIsTripRouteStarted(false);
      } else {
        setMockRides(ridesList);
      }
    } catch (error) {
      console.error("Failed to load rides", error);
    }
  };

  const acceptRide = async (shipmentId) => {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);
    try {
      if (!mobile_number) {
        throw new Error("Mobile number is required to accept ride");
      }

      const response = await fetch(`${URL}delivery-partner/accept-ride`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify({ tripId: shipmentId, mobile: mobile_number }),
      });

      if (response.status === 409) {
        const errorData = await response.json();
        const conflictError = new Error(
          errorData.message || "This trip is no longer available."
        );
        conflictError.status = 409;
        throw conflictError;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to accept ride");
      }

      return await response.json();
    } catch (error) {
      console.error("Accept ride failed:", error.message);
      throw error;
    }
  };

  const handleAcceptRide = async (ride) => {
    try {
      const responseData = await acceptRide(ride._id);
      const updatedRide = responseData?.data || ride;
      setAcceptedTripData(updatedRide);
    } catch (error) {
      if (error.status === 409) {
        alert(error.message || "Trip already taken. Refreshing trips...");
        await fetchRides(mobile_number);
      } else {
        alert(error.message);
      }
      throw error;
    }
  };

  const handleProceedToActiveRide = () => {
    setIsProceeding(true);
    try {
      sessionStorage.removeItem("trip_route_started");
      setIsTripRouteStarted(false);

      if (acceptedTripData) {
        setActiveRide(formatRideToActiveRide(acceptedTripData));
        setMockRides([]);
        setIsTripCompleteLocal(false);
      } else {
        loadActiveRide(mobile_number);
      }
    } finally {
      setAcceptedTripData(null);
      setIsProceeding(false);
    }
  };

  const handleRefreshDistance = () => {
    setLoadingDistance(true);
    requestLiveLocation();
    setTimeout(() => setLoadingDistance(false), 500);
  };

  const handleFinalStepComplete = () => {
    setIsTripCompleteLocal(true);
  };

  const handleStartTripOverview = () => {
    sessionStorage.setItem("trip_route_started", "true");
    setIsTripRouteStarted(true);
  };

  const stepsList = activeRide?.steps || [];
  const completedCount = isTripCompleteLocal
    ? stepsList.length
    : stepsList.filter((s) => {
        const st = s.status ? s.status.toUpperCase() : "";
        return (
          st === "COMPLETED" ||
          st === "SKIPPED" ||
          (s.type === "PICKUP" &&
            (st === "IN_PROGRESS" || st === "IN PROGESS"))
        );
      }).length;

  const overviewStops = useMemo(() => {
    let pCount = 0;
    let dCount = 0;
    return stepsList.map((s) => {
      if (s.type === "PICKUP") {
        pCount += 1;
        return {
          ...s,
          label: `P${pCount}`,
          title: `Pickup ${pCount}`,
          isPickup: true,
        };
      }
      dCount += 1;
      return {
        ...s,
        label: `D${dCount}`,
        title: `Drop-off ${dCount}`,
        isPickup: false,
      };
    });
  }, [stepsList]);

  return (
    <div className="min-h-screen bg-slate-100 sm:py-4">
      <div
        className="w-full max-w-md mx-auto min-h-screen sm:min-h-[90vh] bg-white sm:rounded-3xl sm:shadow-xl sm:border overflow-hidden relative flex flex-col justify-between"
        style={{
          borderColor: theme.colors.grey005,
          backgroundColor: theme.colors.offWhite,
        }}
      >
        {/* ----------------- TRIP ACCEPTED MODAL ----------------- */}
        {acceptedTripData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
            <div
              className="w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-2xl border flex flex-col items-center space-y-4 animate-scaleUp"
              style={{
                borderColor: theme.colors.grey002,
                borderRadius: `${theme.radius[24]}px`,
              }}
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center border shadow-sm"
                style={{
                  backgroundColor: `${theme.colors.success}15`,
                  borderColor: `${theme.colors.success}30`,
                }}
              >
                <CircleCheck
                  className="w-9 h-9"
                  style={{ color: theme.colors.success }}
                />
              </div>

              <div className="space-y-1">
                <h3
                  className="text-xl font-bold leading-tight"
                  style={{
                    fontFamily: "BarlowCondensed-Bold",
                    color: theme.colors.bitumen,
                  }}
                >
                  Trip Accepted!
                </h3>
                <p
                  className="text-xs"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.concrete,
                  }}
                >
                  Trip assigned successfully. View your trip stops.
                </p>
              </div>

              <button
                type="button"
                onClick={handleProceedToActiveRide}
                disabled={isProceeding}
                className="w-full py-3.5 text-white font-bold transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  fontFamily: "DMSans-Medium",
                  borderRadius: `${theme.radius[16]}px`,
                  backgroundColor: theme.colors.brand,
                  boxShadow: `0 4px 14px ${theme.colors.brand}40`,
                }}
              >
                <span>{isProceeding ? "Loading Route..." : "View Trip Details"}</span>
                {!isProceeding && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        {/* ----------------- 1. PINNED HEADER ----------------- */}
        {activeRide && isTripRouteStarted ? (
          <header
            className="sticky top-0 z-40 border-b shadow-xs pb-1"
            style={{
              backgroundColor: theme.colors.pureWhite,
              borderColor: theme.colors.grey002,
            }}
          >
            <div
              className="pb-2 mb-1 border-b flex items-center justify-between px-4 pt-3"
              style={{ borderColor: theme.colors.grey005 }}
            >
              <div className="min-w-0 pr-2">
                <h1
                  className="text-base font-black leading-tight tracking-wide truncate text-gray-900"
                  style={{
                    fontFamily: "BarlowCondensed-Bold",
                  }}
                >
                  Trip #{activeRide?.tripNumber || activeRide?.shipmentNumber || "TR-000482"}
                </h1>
                <p
                  className="text-[11px] font-medium text-gray-500 mt-0.5"
                  style={{
                    fontFamily: "DMSans-Regular",
                  }}
                >
                  {completedCount} of {stepsList.length} stops
                </p>
              </div>

              <div className="shrink-0">
                <button
                  type="button"
                  onClick={() => setShowSupportModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-150 active:scale-95 cursor-pointer shadow-2xs hover:bg-gray-50"
                  style={{
                    backgroundColor: theme.colors.offWhite,
                    borderColor: theme.colors.grey002,
                  }}
                >
                  <Headphones className="w-3.5 h-3.5 text-black" />
                  <span
                    className="text-xs font-bold"
                    style={{
                      fontFamily: "DMSans-SemiBold",
                      color: theme.colors.bitumen,
                    }}
                  >
                    Help
                  </span>
                </button>
              </div>
            </div>

            <TripStepperTimeline
              steps={stepsList}
              isTripComplete={isTripCompleteLocal}
            />
          </header>
        ) : (
          <div className="sticky top-0 z-40 bg-white shadow-xs">
            <Navbar />
          </div>
        )}

        {/* ----------------- 2. CONTENT AREA ----------------- */}
        <main className="px-4 py-3 flex-1 overflow-y-auto no-scrollbar space-y-3">
          {activeRide ? (
            !isTripRouteStarted ? (
              /* SCREEN 1: TRIP OVERVIEW / ASSIGNED SCREEN */
              <div className="flex flex-col justify-between min-h-full space-y-4 animate-fadeIn">
                <div className="space-y-3.5">
                  <div>
                    <h2
                      className="text-2xl font-black tracking-tight leading-tight text-gray-900"
                      style={{ fontFamily: "BarlowCondensed-Bold" }}
                    >
                      Trip {activeRide?.tripNumber || activeRide?.shipmentNumber || ""}
                    </h2>
                    <p
                      className="text-xs text-gray-500 font-medium mt-0.5"
                      style={{ fontFamily: "DMSans-Regular" }}
                    >
                      {stepsList.filter((s) => s.type === "PICKUP").length} pickups •{" "}
                      {stepsList.filter((s) => s.type === "DROPOFF").length} drop-offs •{" "}
                      {stepsList.length} stops
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 p-3 bg-white rounded-2xl border border-gray-200 shadow-xs text-center">
                    <div className="flex flex-col items-center justify-center border-r border-gray-100 pr-1">
                      <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        Trip Fare
                      </span>
                      <span
                        className="text-lg font-black text-gray-900 mt-0.5"
                        style={{ fontFamily: "BarlowCondensed-Bold" }}
                      >
                        ₹{activeRide?.earnings || "0"}
                      </span>
                    </div>

                    <div className="flex flex-col items-center justify-center border-r border-gray-100 px-1">
                      <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        Total Distance
                      </span>
                      <span
                        className="text-lg font-black text-gray-900 mt-0.5"
                        style={{ fontFamily: "BarlowCondensed-Bold" }}
                      >
                        {activeRide?.totalDistance || activeRide?.route?.totalDistance || "0"} km
                      </span>
                    </div>

                    <div className="flex flex-col items-center justify-center pl-1">
                      <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        Est. Time
                      </span>
                      <span
                        className="text-lg font-black text-gray-900 mt-0.5"
                        style={{ fontFamily: "BarlowCondensed-Bold" }}
                      >
                        {(() => {
                          const dist = parseFloat(activeRide?.totalDistance || activeRide?.route?.totalDistance || "0");
                          const mins = Math.round((dist / 20) * 60) + stepsList.length * 8 || 45;
                          const h = Math.floor(mins / 60);
                          const m = mins % 60;
                          return h > 0 ? `${h} hr ${m} min` : `${m} min`;
                        })()}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs space-y-3">
                    <h3
                      className="text-xs font-black uppercase tracking-wider text-gray-900"
                      style={{ fontFamily: "BarlowCondensed-Bold" }}
                    >
                      Stops ({stepsList.length})
                    </h3>

                    <div className="relative pl-1">
                      {overviewStops.map((s, idx) => {
                        const isFirst = idx === 0;
                        const isLast = idx === overviewStops.length - 1;

                        return (
                          <div key={s.stop_id || s.step || idx} className="relative flex items-start gap-3.5 pb-6 last:pb-1">
                            {!isLast && (
                              <div
                                className="absolute left-[17px] top-8 w-0.5 bg-gray-200"
                                style={{ height: "calc(100% - 14px)" }}
                              />
                            )}

                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 z-10 shadow-xs"
                              style={{
                                backgroundColor: isFirst
                                  ? theme.colors.brand
                                  : s.isPickup
                                  ? "#475569"
                                  : "#334155",
                                color: "#FFFFFF",
                                fontFamily: "DMMono-Medium",
                                border: isFirst ? `2px solid ${theme.colors.brand}` : "2px solid #CBD5E1",
                              }}
                            >
                              {s.label}
                            </div>

                            <div className="min-w-0 flex-1 pt-0.5">
                              <div className="flex items-center justify-between gap-2">
                                <h4
                                  className="text-xs font-black text-gray-900 leading-tight truncate"
                                  style={{ fontFamily: "DMSans-SemiBold" }}
                                >
                                  {s.title}
                                </h4>
                                {s.distance && (
                                  <span className="text-[11px] font-mono font-medium text-gray-400 shrink-0">
                                    {s.distance} km
                                  </span>
                                )}
                              </div>

                              {/* Full Unclipped Address */}
                              <p
                                className="text-xs text-gray-500 mt-0.5 leading-snug break-words"
                                style={{ fontFamily: "DMSans-Regular" }}
                              >
                                {s.address || s.area || "Address not specified"}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Bottom Action: Swipe to Start Trip */}
                <div className="pt-2 sticky bottom-0 bg-transparent">
                  <SwipeButton
                    text="Swipe to Start Trip"
                    activeText="Starting Trip..."
                    onSwipeSuccess={handleStartTripOverview}
                  />
                </div>
              </div>
            ) : (
              /* Active Stop Journey (Screens 2 through 6) */
              <ActiveRidePage
                activeRide={activeRide}
                currentLocation= {currentLocation}
                GetRideDetails={() => loadActiveRide(mobile_number)}
                mobile={mobile_number}
                onGetNewRides={() => {
                  sessionStorage.removeItem("trip_route_started");
                  setIsTripRouteStarted(false);
                  loadActiveRide(mobile_number);
                }}
                onFinalStepComplete={handleFinalStepComplete}
              />
            )
          ) : mockRides.length > 0 ? (
            /* Available Trips List */
            mockRides.map((ride) => {
              const isAssignedToMe =
                (ride.status?.toUpperCase() === "ASSIGNED" ||
                  ride.assignmentStatus?.toUpperCase() === "ASSIGNED") &&
                ride.rider &&
                String(ride.rider.mobile) === String(mobile_number) &&
                ride.rider.assigned === true;

              return (
                <div
                  key={ride._id || ride.id}
                  className="border transition-all shadow-xs overflow-hidden"
                  style={{
                    backgroundColor: theme.colors.pureWhite,
                    borderRadius: `${theme.radius[24]}px`,
                    borderColor: theme.colors.grey002,
                  }}
                >
                  <div className="p-4 space-y-3.5">
                    <div
                      className="flex items-center justify-between pb-3 border-b"
                      style={{ borderColor: theme.colors.grey005 }}
                    >
                      <div>
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.concrete,
                          }}
                        >
                          Trip Distance
                        </p>
                        <p
                          className="text-base font-extrabold leading-tight mt-0.5"
                          style={{
                            fontFamily: "BarlowCondensed-Bold",
                            color: theme.colors.bitumen,
                          }}
                        >
                          {ride.route?.totalDistance || ride.rideDistance || "0 km"}
                        </p>
                      </div>

                      <div className="text-right">
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.concrete,
                          }}
                        >
                          Distance to 1st Pickup
                        </p>
                        <button
                          onClick={handleRefreshDistance}
                          disabled={loadingDistance}
                          title="Tap to refresh distance"
                          className="inline-flex items-center gap-1.5 mt-0.5 transition-opacity disabled:opacity-50 cursor-pointer active:scale-95"
                          style={{
                            fontFamily: "DMMono-Medium",
                            fontSize: "13px",
                            color: theme.colors.primaryRed,
                          }}
                        >
                          <span>
                            {loadingDistance
                              ? "Updating..."
                              : calculatePickupDistance(ride)}
                          </span>
                          <FontAwesomeIcon
                            icon={faRotateRight}
                            className={`text-[10px] ${
                              loadingDistance ? "animate-spin" : ""
                            }`}
                            style={{ color: theme.colors.primaryRed }}
                          />
                        </button>
                      </div>
                    </div>

                    <div
                      className="rounded-2xl p-3.5 space-y-3 border"
                      style={{
                        backgroundColor: theme.colors.grey001,
                        borderColor: theme.colors.grey005,
                      }}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <FontAwesomeIcon
                              icon={faShop}
                              className="text-xs"
                              style={{ color: theme.colors.charcoal }}
                            />
                            <p
                              className="text-[11px] font-bold uppercase tracking-wider"
                              style={{
                                fontFamily: "BarlowCondensed-SemiBold",
                                color: theme.colors.bitumen,
                              }}
                            >
                              Pickups ({ride.route?.pickups?.length || 0})
                            </p>
                          </div>
                          <ul className="space-y-2">
                            {ride.route?.pickups?.map((p, idx) => (
                              <li key={idx} className="flex items-start gap-1.5">
                                <span
                                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                                  style={{ backgroundColor: theme.colors.bitumen }}
                                />
                                <div className="min-w-0">
                                  <p
                                    className="text-xs font-semibold leading-tight line-clamp-2"
                                    style={{
                                      fontFamily: "DMSans-Medium",
                                      color: theme.colors.bitumen,
                                    }}
                                  >
                                    {p.name}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <FontAwesomeIcon
                              icon={faTruck}
                              className="text-xs"
                              style={{ color: theme.colors.primaryRed }}
                            />
                            <p
                              className="text-[11px] font-bold uppercase tracking-wider"
                              style={{
                                fontFamily: "BarlowCondensed-SemiBold",
                                color: theme.colors.bitumen,
                              }}
                            >
                              Drop-offs ({ride.route?.dropoffs?.length || 0})
                            </p>
                          </div>
                          <ul className="space-y-2">
                            {ride.route?.dropoffs?.map((d, idx) => (
                              <li key={idx} className="flex items-start gap-1.5">
                                <span
                                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                                  style={{ backgroundColor: theme.colors.primaryRed }}
                                />
                                <div className="min-w-0">
                                  <p
                                    className="text-xs font-semibold leading-tight line-clamp-2"
                                    style={{
                                      fontFamily: "DMSans-Medium",
                                      color: theme.colors.bitumen,
                                    }}
                                  >
                                    {d.name}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div
                        className="rounded-xl py-2 px-3 text-center border flex flex-col justify-center items-center"
                        style={{
                          backgroundColor: theme.colors.grey001,
                          borderColor: theme.colors.grey005,
                        }}
                      >
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.concrete,
                          }}
                        >
                          Total Items
                        </p>
                        <p
                          className="text-base font-bold leading-tight mt-0.5"
                          style={{
                            fontFamily: "DMMono-Medium",
                            color: theme.colors.bitumen,
                          }}
                        >
                          {ride.items || 1}
                        </p>
                      </div>

                      <div
                        className="rounded-xl py-2 px-3 text-center border flex flex-col justify-center items-center"
                        style={{
                          backgroundColor: `${theme.colors.success}10`,
                          borderColor: `${theme.colors.success}30`,
                        }}
                      >
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.success,
                          }}
                        >
                          Trip Fare
                        </p>
                        <p
                          className="text-lg font-black leading-tight mt-0.5"
                          style={{
                            fontFamily: "DMMono-Medium",
                            color: theme.colors.success,
                          }}
                        >
                          {ride.earnings || "₹0"}
                        </p>
                      </div>
                    </div>

                    {isAssignedToMe ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveRide(formatRideToActiveRide(ride));
                          setMockRides([]);
                          setIsTripCompleteLocal(false);
                          sessionStorage.removeItem("trip_route_started");
                          setIsTripRouteStarted(false);
                        }}
                        className="w-full py-3.5 text-white font-bold transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer"
                        style={{
                          fontFamily: "DMSans-Medium",
                          borderRadius: `${theme.radius[16]}px`,
                          backgroundColor: theme.colors.brand,
                          boxShadow: `0 4px 14px ${theme.colors.brand}40`,
                        }}
                      >
                        <span>View Trip Overview</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <SwipeButton
                        text="Swipe to Accept Trip"
                        onSwipeSuccess={() => handleAcceptRide(ride)}
                      />
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div
              className="border p-6 text-center shadow-xs"
              style={{
                backgroundColor: theme.colors.pureWhite,
                borderRadius: `${theme.radius[24]}px`,
                borderColor: theme.colors.grey002,
              }}
            >
              <p
                className="text-base font-bold"
                style={{
                  fontFamily: "BarlowCondensed-Bold",
                  color: theme.colors.bitumen,
                }}
              >
                No trips available now
              </p>
              <p
                className="text-xs mt-1"
                style={{
                  fontFamily: "DMSans-Regular",
                  color: theme.colors.concrete,
                }}
              >
                Please wait for new trips to be assigned.
              </p>
            </div>
          )}
        </main>

        <HelpSupportModal
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          activeRide={activeRide}
          currentStep={stepsList.find((s) => s.status !== "COMPLETED")}
        />
      </div>
    </div>
  );
};

export default Rides;